import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';

interface IntroAnimationProps {
  onComplete: () => void;
}

const Particle = ({ index }: { index: number }) => {
  const angle = Math.random() * Math.PI * 2;
  const radius = 60 + Math.random() * 160;
  const x = Math.cos(angle) * radius;
  const y = Math.sin(angle) * radius;
  const size = 0.5 + Math.random() * 1.5;
  const delay = Math.random() * 1.5;
  const duration = 1.5 + Math.random() * 2;

  return (
    <motion.div
      key={index}
      className="absolute rounded-full bg-[#FBEAD7]"
      style={{
        width: size,
        height: size,
        left: '50%',
        top: '50%',
        x,
        y,
      }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity: [0, 0.6, 0],
        scale: [0, 1, 0],
      }}
      transition={{
        duration,
        delay,
        ease: 'easeInOut',
        repeat: 1,
        repeatDelay: 0.5,
      }}
    />
  );
};

export const IntroAnimation = ({ onComplete }: IntroAnimationProps) => {
  const [phase, setPhase] = useState<'intro' | 'exit'>('intro');
  const [showParticles, setShowParticles] = useState(false);
  const [showLine, setShowLine] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [showUnderline, setShowUnderline] = useState(false);
  const lineRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Phase 1: Show particles
    const t1 = setTimeout(() => setShowParticles(true), 150);
    // Phase 2: Show horizontal line
    const t2 = setTimeout(() => setShowLine(true), 400);
    // Phase 3: Reveal logo
    const t3 = setTimeout(() => setShowLogo(true), 700);
    // Phase 4: Draw underline
    const t4 = setTimeout(() => setShowUnderline(true), 1200);
    // Phase 5: Hold then exit
    const t5 = setTimeout(() => setPhase('exit'), 1800);
    // Phase 6: Call complete
    const t6 = setTimeout(() => onComplete(), 2300);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
      clearTimeout(t6);
    };
  }, [onComplete]);

  const letterVariants = {
    hidden: { opacity: 0, y: 12, filter: 'blur(8px)' },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        delay: i * 0.07,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  const xoLetterVariants = {
    hidden: { opacity: 0, y: 8, filter: 'blur(12px)' },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        delay: 0.5 + i * 0.12,
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  return (
    <motion.div
      key="intro-root"
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-[#060606]"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Ambient Background Glow */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, delay: 0.5 }}
        style={{
          background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(66,0,255,0.04) 0%, transparent 70%)',
        }}
      />

      {/* Particles */}
      {showParticles && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <Particle key={i} index={i} />
          ))}
        </div>
      )}

      {/* Horizontal ambient line */}
      <motion.div
        className="absolute"
        style={{ width: '320px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(251,234,215,0.2), transparent)' }}
        initial={{ scaleX: 0, opacity: 0 }}
        animate={showLine ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Logo Container */}
      <div ref={logoRef} className="relative flex flex-col items-center gap-5">
        {/* Logo Image */}
        {showLogo && (
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Light sweep over logo */}
            <motion.div
              className="absolute inset-0 z-10 pointer-events-none"
              initial={{ x: '-100%', opacity: 0 }}
              animate={{ x: '120%', opacity: [0, 0.6, 0] }}
              transition={{ duration: 1.0, ease: 'easeInOut', delay: 0.3 }}
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(66,0,255,0.3), transparent)',
                filter: 'blur(8px)',
              }}
            />
            <img
              src="/assets/logo.png"
              alt="EVOLVE XO"
              className="h-20 md:h-28 w-auto object-contain relative z-0"
              style={{ filter: 'drop-shadow(0 0 40px rgba(66,0,255,0.2))' }}
            />
          </motion.div>
        )}

        {/* Underline */}
        {showUnderline && (
          <motion.div
            className="h-px"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(66,0,255,0.6), rgba(251,234,215,0.4), transparent)',
              width: '200px',
            }}
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          />
        )}
      </div>
    </motion.div>
  );
};

export default IntroAnimation;
